import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, TextField, Typography } from "@mui/material";
import { ArrowForward, Close, Done, FolderOpen } from "@mui/icons-material";
import { useMutation } from "@apollo/client";

import { SalesAidsStyles } from "./SalesAidsStyles";
import Theme from "../../../../../utils/Theme";
import { generateRandomId } from "../../../../../utils/Utils";
import AddIconUtils from "../../../../../utils/AddIconUtils";
import { GET_VIDEO_UPLOAD_URL } from "../../../../../graphql/mutation/mutation";
import {
  uploadVideoFileToS3,
  rejectExternalEmbedUrl,
} from "../../../../../utils/videoPlayer";

const VideoArtifact = ({
  artifacts,
  setArtifacts,
  handleArrowClick,
  handleRemove,
  edit,
}) => {
  const [dragOver, setDragOver] = useState({});
  const [uploading, setUploading] = useState({});
  const [urlErrors, setUrlErrors] = useState({});
  const fileInputRefs = useRef({});
  const [getVideoUploadUrl] = useMutation(GET_VIDEO_UPLOAD_URL);

  // Validate existing YouTube/Vimeo URLs when artifacts load
  useEffect(() => {
    const next = {};
    (artifacts || []).forEach((item, index) => {
      next[`${index}_url`] = rejectExternalEmbedUrl(item?.url);
      next[`${index}_es_url`] = rejectExternalEmbedUrl(item?.es_url);
    });
    setUrlErrors(next);
  }, [artifacts]);

  const handleTextChange = (index, field, value) => {
    if (field === "url" || field === "es_url") {
      const err = rejectExternalEmbedUrl(value);
      setUrlErrors((prev) => ({
        ...prev,
        [`${index}_${field}`]: err,
      }));
    }

    setArtifacts((prev) => ({
      ...prev,
      video: prev?.video?.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: value,
              isEdit: true,
            }
          : item
      ),
    }));
  };

  const handleAddNew = () => {
    const newVideoArtifact = {
      url: "",
      title: "",
      es_url: "",
      es_title: "",
      position: null,
      artifactId: generateRandomId(6),
      isEdit: false,
    };
    setArtifacts((prev) => ({
      ...prev,
      video: [...prev?.video, newVideoArtifact],
    }));
  };

  const handleFileSelect = async (index, urlField, titleField, file) => {
    if (!file) return;
    const key = `${index}_${urlField}`;
    setUploading((prev) => ({ ...prev, [key]: true }));
    try {
      const { publicUrl, fileName } = await uploadVideoFileToS3(
        getVideoUploadUrl,
        file,
      );

      setArtifacts((prev) => ({
        ...prev,
        video: prev?.video?.map((item, i) => {
          if (i !== index) return item;
          const next = {
            ...item,
            [urlField]: publicUrl,
            isEdit: true,
          };
          // Set title from filename only when empty
          if (!item[titleField]?.trim()) {
            next[titleField] = fileName;
          }
          return next;
        }),
      }));
      setUrlErrors((prev) => ({ ...prev, [key]: null }));
    } catch (err) {
      alert(err?.message || "Upload failed");
    } finally {
      setUploading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleDragOver = (e, key) => {
    e.preventDefault();
    setDragOver((prev) => ({ ...prev, [key]: true }));
  };

  const handleDragLeave = (key) => {
    setDragOver((prev) => ({ ...prev, [key]: false }));
  };

  const handleDrop = (e, index, urlField, titleField) => {
    e.preventDefault();
    const key = `${index}_${urlField}`;
    setDragOver((prev) => ({ ...prev, [key]: false }));
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(index, urlField, titleField, file);
    }
  };

  const renderUploadZone = (index, urlField, titleField) => {
    const key = `${index}_${urlField}`;
    const isOver = dragOver[key];
    const isBusy = uploading[key];

    return (
      <>
        <input
          type="file"
          accept="video/mp4"
          style={{ display: "none" }}
          ref={(el) => (fileInputRefs.current[key] = el)}
          onChange={(e) =>
            handleFileSelect(index, urlField, titleField, e.target.files?.[0])
          }
        />
        <Box
          onClick={() => !isBusy && fileInputRefs.current[key]?.click()}
          onDragOver={(e) => handleDragOver(e, key)}
          onDragLeave={() => handleDragLeave(key)}
          onDrop={(e) => handleDrop(e, index, urlField, titleField)}
          sx={{
            width: 130,
            minWidth: 130,
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: isBusy ? "wait" : "pointer",
            bgcolor: isOver ? `${Theme.palette.primary.main}10` : "transparent",
            transition: "background-color 0.2s",
            gap: 0.5,
            opacity: isBusy ? 0.7 : 1,
          }}
        >
          {isBusy ? (
            <CircularProgress size={20} />
          ) : (
            <FolderOpen
              sx={{
                fontSize: 20,
                color: isOver ? Theme.palette.primary.main : "rgba(0,0,0,0.4)",
              }}
            />
          )}
          <Typography
            sx={{
              fontSize: 14,
              color: isOver ? Theme.palette.primary.main : "rgba(0,0,0,0.4)",
              textAlign: "center",
              lineHeight: 1.2,
            }}
          >
            {isBusy ? "Uploading…" : "Upload"}
          </Typography>
        </Box>
      </>
    );
  };

  return (
    <Box display={"flex"} flexDirection={"column"}>
      {artifacts?.map((item, index) => {
        const hasEnglishTitle = item.title?.trim() !== "";
        const hasEnglishUrl = item.url?.trim() !== "";
        const englishComplete = hasEnglishTitle && hasEnglishUrl;

        const hasSpanishTitle = item.es_title?.trim() !== "";
        const hasSpanishUrl = item.es_url?.trim() !== "";
        const spanishComplete = hasSpanishTitle && hasSpanishUrl;

        const hasBlockedUrl =
          !!rejectExternalEmbedUrl(item.url) ||
          !!rejectExternalEmbedUrl(item.es_url);

        const isValid =
          !hasBlockedUrl &&
          (englishComplete || spanishComplete) &&
          (!hasEnglishTitle || englishComplete) &&
          (!hasEnglishUrl || englishComplete) &&
          (!hasSpanishTitle || spanishComplete) &&
          (!hasSpanishUrl || spanishComplete);

        return (
          <Box
            key={item?.id || item?.artifactId}
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
              p: "6px",
              borderRadius: 2,
              alignItems: "flex-start",
              bgcolor: item?.isEdit ? "#FFF7F7" : null,
              width: "100%",
            }}
          >
            <Close
              sx={{
                ...SalesAidsStyles.closeIcon,
                color: Theme.palette.error.main,
              }}
              onClick={() => handleRemove(item, "video")}
            />

            <Box sx={{ ...SalesAidsStyles.subheadingBox, flex: 1 }}>
              <Typography sx={SalesAidsStyles.wordStyle}>English</Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  alignItems: "stretch",
                  width: "100%",
                }}
              >
                <TextField
                  sx={{ flex: 1 }}
                  variant="outlined"
                  label="Title"
                  onChange={(e) =>
                    handleTextChange(index, "title", e.target.value)
                  }
                  value={item.title}
                  multiline
                  minRows={2}
                  size="small"
                />
                {renderUploadZone(index, "url", "title")}
              </Box>

              <TextField
                fullWidth
                variant="outlined"
                label="URL"
                value={item.url}
                onChange={(e) => handleTextChange(index, "url", e.target.value)}
                multiline
                minRows={2}
                size="small"
                sx={{ mt: 1 }}
                error={
                  !!(
                    urlErrors[`${index}_url`] ||
                    rejectExternalEmbedUrl(item.url)
                  )
                }
                helperText={
                  urlErrors[`${index}_url`] ||
                  rejectExternalEmbedUrl(item.url) ||
                  "YouTube, Vimeo, or direct MP4/HLS URL"
                }
                FormHelperTextProps={{
                  sx:
                    urlErrors[`${index}_url`] ||
                    rejectExternalEmbedUrl(item.url)
                      ? { color: "error.main", fontWeight: 600 }
                      : undefined,
                }}
              />
            </Box>

            <Box sx={{ ...SalesAidsStyles.subheadingBox, flex: 1 }}>
              <Typography sx={SalesAidsStyles.wordStyle}>Spanish</Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  alignItems: "stretch",
                  width: "100%",
                }}
              >
                <TextField
                  sx={{ flex: 1 }}
                  variant="outlined"
                  label="Título"
                  onChange={(e) =>
                    handleTextChange(index, "es_title", e.target.value)
                  }
                  value={item.es_title || ""}
                  multiline
                  minRows={2}
                  size="small"
                />
                {renderUploadZone(index, "es_url", "es_title")}
              </Box>

              <TextField
                fullWidth
                variant="outlined"
                label="Spanish URL"
                onChange={(e) =>
                  handleTextChange(index, "es_url", e.target.value)
                }
                value={item.es_url || ""}
                multiline
                minRows={2}
                size="small"
                sx={{ mt: 1 }}
                error={
                  !!(
                    urlErrors[`${index}_es_url`] ||
                    rejectExternalEmbedUrl(item.es_url)
                  )
                }
                helperText={
                  urlErrors[`${index}_es_url`] ||
                  rejectExternalEmbedUrl(item.es_url) ||
                  "YouTube, Vimeo, or direct MP4/HLS URL"
                }
                FormHelperTextProps={{
                  sx:
                    urlErrors[`${index}_es_url`] ||
                    rejectExternalEmbedUrl(item.es_url)
                      ? { color: "error.main", fontWeight: 600 }
                      : undefined,
                }}
              />
            </Box>

            {item?.isEdit ? (
              <Done
                sx={{
                  fontSize: 14,
                  mt: 4,
                  alignSelf: "center",
                  color: isValid
                    ? Theme.palette.primary.main
                    : Theme.palette.action.disabled,
                  cursor: isValid ? "pointer" : "not-allowed",
                }}
                onClick={() => isValid && handleArrowClick(item, "video")}
              />
            ) : (
              <ArrowForward
                sx={{
                  fontSize: 14,
                  mt: 4,
                  alignSelf: "center",
                  color: isValid
                    ? Theme.palette.primary.main
                    : Theme.palette.action.disabled,
                  cursor: isValid ? "pointer" : "not-allowed",
                }}
                onClick={() => isValid && handleArrowClick(item, "video")}
              />
            )}
          </Box>
        );
      })}
      <AddIconUtils
        artifacts={artifacts}
        handleAddNew={handleAddNew}
        total={20}
      />
    </Box>
  );
};

export default VideoArtifact;
